import { ISubmission } from '../../../constants/types';

export interface VisitShell {
    uuid: string;
    status?: string;
    createdAt?: Date | string | null;
    completedAt?: Date | string | null;
    submittedAt?: Date | string | null;
}

export interface VisitGroup {
    key: string;
    visitStatus?: string;
    visitDate?: Date | string | null;
    indices: number[];
}

/** Prefer the visit that actually has submissions; tie-break by submission count, then date. */
export function pickDefaultExpandedKey(groups: VisitGroup[]): string | null {
    if (groups.length === 0) return null;
    const withData = groups.filter((group) => group.indices.length > 0);
    const pool = withData.length > 0 ? withData : groups;
    const sorted = [...pool].sort((a, b) => {
        const submissionDiff = b.indices.length - a.indices.length;
        if (submissionDiff !== 0) return submissionDiff;
        const aTime = a.visitDate ? new Date(a.visitDate).getTime() : 0;
        const bTime = b.visitDate ? new Date(b.visitDate).getTime() : 0;
        return bTime - aTime;
    });
    return sorted[0].key;
}

/**
 * SKIN-55 — deep-link / shareable URL. Expands the visit named in the query when present.
 */
export function resolveExpandedKeys(groups: VisitGroup[], initialVisitUuid?: string | null): Set<string> {
    if (initialVisitUuid) {
        const target = groups.find((group) => group.key === initialVisitUuid);
        if (target) return new Set([initialVisitUuid]);
    }
    const defaultKey = pickDefaultExpandedKey(groups);
    return defaultKey ? new Set([defaultKey]) : new Set();
}

export function activeVisitUuidFromSearch(search: string): string | null {
    return new URLSearchParams(search).get('visitUuid');
}

export function buildSearchWithVisitUuid(currentSearch: string, visitUuid: string | null): string {
    const params = new URLSearchParams(currentSearch);
    if (visitUuid) {
        params.set('visitUuid', visitUuid);
    } else {
        params.delete('visitUuid');
    }
    const next = params.toString();
    return next ? `?${next}` : '';
}

export function groupSubmissionsByVisit(records: ISubmission[]): VisitGroup[] {
    const groups = new Map<string, VisitGroup>();
    records.forEach((record, index) => {
        const visit = record.visit;
        const key = visit?.uuid || '__no_visit__';
        let group = groups.get(key);
        if (!group) {
            group = {
                key,
                visitStatus: visit?.status,
                visitDate: visit ? (visit.submittedAt ?? visit.completedAt ?? visit.createdAt) : undefined,
                indices: [],
            };
            groups.set(key, group);
        }
        group.indices.push(index);
    });
    return sortVisitGroups(Array.from(groups.values()));
}

export function mergeVisitShells(groups: VisitGroup[], shellVisits: VisitShell[]): VisitGroup[] {
    const byKey = new Map(groups.map((group) => [group.key, group]));
    for (const shell of shellVisits) {
        if (byKey.has(shell.uuid)) continue;
        byKey.set(shell.uuid, {
            key: shell.uuid,
            visitStatus: shell.status,
            visitDate: shell.submittedAt ?? shell.completedAt ?? shell.createdAt,
            indices: [],
        });
    }
    return sortVisitGroups(Array.from(byKey.values()));
}

function sortVisitGroups(groups: VisitGroup[]): VisitGroup[] {
    return groups.sort((a, b) => {
        const aTime = a.visitDate ? new Date(a.visitDate).getTime() : 0;
        const bTime = b.visitDate ? new Date(b.visitDate).getTime() : 0;
        return bTime - aTime;
    });
}

export function buildVisitGroups(records: ISubmission[], shellVisits: VisitShell[] = []): VisitGroup[] {
    return mergeVisitShells(groupSubmissionsByVisit(records), shellVisits);
}

/** Number of filled fields in a submission: non-empty direct records + non-empty field-group records. */
function submissionFilledFieldCount(submission: ISubmission): number {
    const isFilled = (value: unknown) => value !== undefined && value !== null && String(value).trim() !== '';
    const direct = (submission.surveyRecords ?? []).filter((record) => isFilled(record?.value)).length;
    const grouped = (submission.fieldGroups ?? []).reduce(
        (groupAcc, group) =>
            groupAcc +
            (group.instances ?? []).reduce(
                (instanceAcc, instance) =>
                    instanceAcc + (instance.surveyRecords ?? []).filter((record) => isFilled(record?.value)).length,
                0,
            ),
        0,
    );
    return direct + grouped;
}

/**
 * SKIN-119 — of the submissions at `indices` (a visit's submissions), return the
 * global `records` index of the one with the most filled fields. Ties break to the
 * most recent `createdAt`. Returns null when the visit has no submissions.
 */
export function pickRichestSubmissionIndex(records: ISubmission[], indices: number[]): number | null {
    if (!indices || indices.length === 0) return null;
    let best = indices[0];
    let bestCount = submissionFilledFieldCount(records[best]);
    for (let position = 1; position < indices.length; position += 1) {
        const index = indices[position];
        const count = submissionFilledFieldCount(records[index]);
        if (count > bestCount) {
            best = index;
            bestCount = count;
            continue;
        }
        if (count === bestCount) {
            const candidateTime = records[index]?.createdAt ? new Date(records[index].createdAt).getTime() : 0;
            const bestTime = records[best]?.createdAt ? new Date(records[best].createdAt).getTime() : 0;
            if (candidateTime > bestTime) {
                best = index;
            }
        }
    }
    return best;
}
